import { useEffect, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api, keys, type ForumPost, type ForumTag, type Named, type Uuid } from "@shared/api";
import { RenameThread, useStore } from "@features/chat";
import { plainText } from "@shared/lib/markup";
import { dayTime, plural } from "@shared/lib/format";
import { fromSelf, livePerson, Tile } from "@entities/user";
import { useMe } from "@entities/session";
import { Dialog } from "@shared/ui/bits";
import { MenuOn, SEPARATOR, type MenuItem } from "@shared/ui/Menu";
import { messageLink } from "@shared/lib/jump";
import { readDraft, writeDraft } from "@shared/lib/desktop";
import { useRestartPreparation } from "@features/updater";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Textarea } from "@shared/ui/textarea";
import { IconForumChannel } from "@shared/ui/icons";

const TITLE_MAX = 100;
const PAGE = 50;
const POST_TAGS_MAX = 5;
const TAGS_MAX = 20;

/**
 * Форум — как в Discord: вместо ленты посты. Пост открывается своей веткой в
 * правой колонке — там и обсуждение, и первое сообщение целиком.
 */
export function ForumView({
  channelId,
  title,
  canSend,
  canManage,
  canModerate = false,
  serverId = null,
  people,
  guidelines = null,
}: {
  channelId: Uuid;
  /** «Рекомендации к публикациям» — тема форума, как в Discord: над формой нового поста. */
  guidelines?: string | null;
  /** Для ссылки на пост. */
  serverId?: Uuid | null;
  title: string;
  canSend: boolean;
  /** Управлять каналом — править набор меток. */
  canManage: boolean;
  /** Управлять сообщениями — закрывать и открывать посты. */
  canModerate?: boolean;
  people: Map<Uuid, Named>;
}) {
  const store = useStore();
  const cache = useQueryClient();
  const me = fromSelf(useMe());
  const [writing, setWriting] = useState(false);
  const [tagging, setTagging] = useState(false);
  /** Показывать только посты с этой меткой; ничего — все. */
  const [only, setOnly] = useState<Uuid | null>(null);
  /** Порядок — как «Сортировать» в Discord: по последней активности или по дате создания. */
  const [sort, setSort] = useState<"activity" | "created">("activity");
  // Страницами по 50: где что-то было последним — сверху, дальше — «Показать ещё».
  const { data, isLoading, isError, dataUpdatedAt, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [...keys.posts(channelId), sort],
      initialPageParam: 0,
      queryFn: ({ pageParam }) => api.messages.posts(channelId, pageParam, sort),
      getNextPageParam: (last, pages) => (last.items.length < PAGE ? undefined : pages.length * PAGE),
    });
  const tags = data?.pages[0]?.tags ?? [];
  const named = new Map(tags.map((tag) => [tag.id, tag.name]));
  // Метку убрали из набора — фильтр по ней сам сбрасывается на «все».
  const filter = only && named.has(only) ? only : null;
  // Пост мог сдвинуться между страницами, пока их листали, — повтор не показываем.
  const seen = new Set<Uuid>();
  const posts = (data?.pages ?? [])
    .flatMap((page) => page.items)
    .filter((post) => !seen.has(post.id) && seen.add(post.id))
    .filter((post) => !filter || post.tag_ids.includes(filter));

  /*
   * Ленты у форума нет, и отмечать прочитанное ей нечем: открытый форум с
   * каждым свежим списком отмечает прочитанным себя сам. Иначе счётчик рос бы
   * прямо на глазах у того, кто смотрит.
   */
  useEffect(() => {
    if (dataUpdatedAt && document.visibilityState === "visible") store.markRead(channelId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, dataUpdatedAt]);

  const open = (post: ForumPost) =>
    post.root_message_id && store.openThread(channelId, post.root_message_id, post.id);

  const refresh = () => cache.invalidateQueries({ queryKey: keys.posts(channelId) });
  /** Чей пост спрашивают удалить. */
  const [doomed, setDoomed] = useState<ForumPost | null>(null);
  /** Пост, которому меняют заголовок. */
  const [retitling, setRetitling] = useState<ForumPost | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const act = (work: Promise<unknown>) =>
    void work
      .then(refresh)
      .catch((error: unknown) => setProblem(error instanceof ApiError ? error.message : "Не получилось"));

  /*
   * Меню карточки — как в Discord: ссылка — всем; закрепить и закрыть — тем,
   * кто управляет сообщениями; удалить — им и автору. Удалить пост значит
   * удалить его первое сообщение: вместе с ним уходят и ответы.
   */
  const postItems = (post: ForumPost): MenuItem[] => {
    const root = post.root_message_id;
    const items: MenuItem[] = [
      {
        label: "Копировать ссылку",
        onPick: () =>
          root &&
          void navigator.clipboard
            ?.writeText(messageLink({ serverId, channelId, messageId: root }))
            .catch(() => {}),
      },
    ];
    if (canModerate) {
      items.push(
        SEPARATOR,
        {
          label: post.pinned ? "Открепить пост" : "Закрепить пост",
          onPick: () => act(api.messages.pinThread(channelId, post.id, !post.pinned)),
        },
        {
          label: post.locked ? "Открыть пост" : "Закрыть пост",
          onPick: () => act(api.messages.lockThread(channelId, post.id, !post.locked)),
        },
      );
    }
    // Заголовок — автору и тем, кто управляет сообщениями, как у «Переименовать ветку».
    if (canModerate || (root && post.author?.id === me?.id)) {
      items.push(SEPARATOR, { label: "Изменить заголовок", onPick: () => setRetitling(post) });
    }
    if (root && (canModerate || post.author?.id === me?.id)) {
      items.push(SEPARATOR, {
        label: "Удалить пост",
        kind: "danger",
        onPick: () => setDoomed(post),
      });
    }
    return items;
  };

  return (
    <div className="forum">
      <div className="forum-head">
        <IconForumChannel size={18} />
        <span className="grow forum-name">{title}</span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          title="Сортировать посты"
          onClick={() => setSort(sort === "activity" ? "created" : "activity")}
        >
          {sort === "activity" ? "Последняя активность" : "Дата создания"}
        </Button>
        {canManage && (
          <Button type="button" size="sm" variant="ghost" onClick={() => setTagging(true)}>
            Метки
          </Button>
        )}
        {canSend && !writing && (
          <Button type="button" size="sm" onClick={() => setWriting(true)}>
            Новый пост
          </Button>
        )}
      </div>

      {tags.length > 0 && (
        <div className="forum-tags" role="group" aria-label="Фильтр по меткам">
          <button type="button" className={`forum-tag${filter === null ? " on" : ""}`} onClick={() => setOnly(null)}>
            Все
          </button>
          {tags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              className={`forum-tag${filter === tag.id ? " on" : ""}`}
              onClick={() => setOnly(filter === tag.id ? null : tag.id)}
            >
              {tag.name}
            </button>
          ))}
        </div>
      )}

      {writing && (
        <PostForm channelId={channelId} tags={tags} guidelines={guidelines} onDone={() => setWriting(false)} />
      )}
      {tagging && <TagsDialog channelId={channelId} tags={tags} onClose={() => setTagging(false)} />}

      {problem && (
        <button type="button" className="small danger-text" onClick={() => setProblem(null)}>
          {problem}
        </button>
      )}
      {retitling && (
        <RenameThread
          channelId={channelId}
          threadId={retitling.id}
          name={retitling.title}
          required
          onDone={() => {
            setRetitling(null);
            void refresh();
          }}
          onClose={() => setRetitling(null)}
        />
      )}
      {doomed?.root_message_id && (
        <Dialog
          title="Удалить пост?"
          onClose={() => setDoomed(null)}
          actions={
            <>
              <Button type="button" variant="secondary" onClick={() => setDoomed(null)}>
                Отмена
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  act(api.messages.remove(channelId, doomed.root_message_id!));
                  setDoomed(null);
                }}
              >
                Удалить
              </Button>
            </>
          }
        >
          <div className="small">
            «{doomed.title}» уйдёт вместе со всеми ответами в нём. Вернуть его не получится.
          </div>
        </Dialog>
      )}

      <div className="forum-list">
        {isError && <div className="side-col-note">Посты не загрузились.</div>}
        {!isLoading && !isError && posts.length === 0 && (
          <div className="side-col-note">
            Постов ещё нет. Пост — это тема с заголовком: обсуждают её в ветке под ним.
          </div>
        )}
        {posts.map((post) => {
          const author = post.author ? livePerson(post.author, people, me) : null;
          return (
            <MenuOn key={post.id} title={post.title} items={postItems(post)}>
            <button type="button" className="forum-post" onClick={() => open(post)}>
              <div className="forum-post-title">
                {post.title}
                {post.pinned && <span className="forum-tag small-tag forum-locked">закреплён</span>}
                {post.locked && <span className="forum-tag small-tag forum-locked">закрыт</span>}
              </div>
              {post.tag_ids.length > 0 && (
                <div className="forum-post-tags">
                  {post.tag_ids
                    .filter((id) => named.has(id))
                    .map((id) => (
                      <span key={id} className="forum-tag small-tag">
                        {named.get(id)}
                      </span>
                    ))}
                </div>
              )}
              <div className="forum-post-line small">
                {author && <Tile person={author} size={18} mark={null} />}
                <span className="forum-post-author">{author?.name ?? "удалено"}</span>
                <span className="muted ellipsis">{post.excerpt ? plainText(post.excerpt) : ""}</span>
              </div>
              <div className="forum-post-meta tiny muted">
                {post.reply_count > 0
                  ? `${post.reply_count} ${plural(post.reply_count, "ответ", "ответа", "ответов")}`
                  : "без ответов"}
                {" · "}
                {dayTime(post.last_activity)}
              </div>
            </button>
            </MenuOn>
          );
        })}
        {hasNextPage && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            style={{ alignSelf: "center" }}
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {isFetchingNextPage ? "Секунду…" : "Показать ещё"}
          </Button>
        )}
      </div>
    </div>
  );
}

function PostForm({
  channelId,
  tags,
  guidelines,
  onDone,
}: {
  channelId: Uuid;
  tags: ForumTag[];
  guidelines: string | null;
  onDone: () => void;
}) {
  const store = useStore();
  const me = useMe();
  // Черновик поста — как у поля ветки: переживает и закрытие формы, и перезапуск обновления.
  const titlePlace = `post-title:${channelId}`;
  const textPlace = `post:${channelId}`;
  const [title, setTitle] = useState(() => readDraft(me.id, titlePlace));
  const [picked, setPicked] = useState<Uuid[]>([]);
  const [content, setContent] = useState(() => readDraft(me.id, textPlace));

  useEffect(() => writeDraft(me.id, titlePlace, title), [me.id, titlePlace, title]);
  useEffect(() => writeDraft(me.id, textPlace, content), [me.id, textPlace, content]);
  useRestartPreparation(() => {
    writeDraft(me.id, titlePlace, title);
    writeDraft(me.id, textPlace, content);
  });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const ready = title.trim().length > 0 && content.trim().length > 0;

  async function publish() {
    setBusy(true);
    setProblem(null);
    try {
      // Набор могли поменять, пока писали пост: метки, которых больше нет, не шлём.
      const known = picked.filter((id) => tags.some((tag) => tag.id === id));
      await store.sendPost(channelId, title.trim(), content.trim(), known);
      writeDraft(me.id, titlePlace, "");
      writeDraft(me.id, textPlace, "");
      onDone();
    } catch (error) {
      setProblem(
        error instanceof ApiError && error.code === "automod"
          ? "АвтоМод сервера не пропустил слово"
          : error instanceof ApiError
            ? error.message
            : "Пост не ушёл",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="forum-form">
      {guidelines && (
        <div className="forum-guidelines">
          <span className="forum-guidelines-title">Рекомендации к публикациям</span>
          <span>{guidelines}</span>
        </div>
      )}
      <Input
        autoFocus
        value={title}
        maxLength={TITLE_MAX}
        placeholder="Заголовок"
        aria-label="Заголовок поста"
        onChange={(event) => setTitle(event.target.value)}
      />
      {tags.length > 0 && (
        <div className="forum-tags" role="group" aria-label="Метки поста">
          {tags.map((tag) => {
            const on = picked.includes(tag.id);
            return (
              <button
                key={tag.id}
                type="button"
                className={`forum-tag${on ? " on" : ""}`}
                aria-pressed={on}
                disabled={!on && picked.length >= POST_TAGS_MAX}
                onClick={() =>
                  setPicked((previous) => (on ? previous.filter((id) => id !== tag.id) : [...previous, tag.id]))
                }
              >
                {tag.name}
              </button>
            );
          })}
        </div>
      )}
      <Textarea
        value={content}
        rows={4}
        placeholder="О чём пост?"
        aria-label="Текст поста"
        onChange={(event) => setContent(event.target.value)}
      />
      <div className="line" style={{ gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
        {problem && <span className="small danger-text grow">{problem}</span>}
        <Button type="button" size="sm" variant="secondary" onClick={onDone}>
          Отмена
        </Button>
        <Button type="button" size="sm" disabled={!ready || busy} onClick={() => void publish()}>
          Опубликовать
        </Button>
      </div>
    </div>
  );
}

/** Набор меток форума — по одной на строку; пропавшие уходят и с постов. */
function TagsDialog({ channelId, tags, onClose }: { channelId: Uuid; tags: ForumTag[]; onClose: () => void }) {
  const cache = useQueryClient();
  const [text, setText] = useState(tags.map((tag) => tag.name).join("\n"));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const names = text
    .split("\n")
    .map((name) => name.trim())
    .filter(Boolean);

  async function save() {
    setBusy(true);
    setProblem(null);
    try {
      await api.messages.setTags(channelId, names);
      await cache.invalidateQueries({ queryKey: keys.posts(channelId) });
      onClose();
    } catch (error) {
      setProblem(
        error instanceof ApiError && error.code === "automod"
          ? "АвтоМод сервера не пропустил слово"
          : error instanceof ApiError
            ? error.message
            : "Не сохранилось",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Метки форума"
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" disabled={busy || names.length > TAGS_MAX} onClick={() => void save()}>
            Сохранить
          </Button>
        </>
      }
    >
      <Textarea
        value={text}
        rows={8}
        aria-label="Метки форума"
        placeholder={"Идея\nБаг\nВопрос"}
        onChange={(event) => setText(event.target.value)}
      />
      <div className="small muted" style={{ marginTop: 8 }}>
        По одной на строку, до {TAGS_MAX}, каждая до 20 знаков. Убранная метка уходит и с постов.
      </div>
      {problem && <div className="small danger-text">{problem}</div>}
    </Dialog>
  );
}
